import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, DollarSign, Calendar, Activity, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissedInsights, setDismissedInsights] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/analytics/insights`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionSelect = (insight, recommendation) => {
    // For now, just log and dismiss
    // In future, this will update calculator settings
    console.log('Action selected:', {
      insight: insight.category,
      action: recommendation.action,
      value: recommendation.value
    });

    if (recommendation.action === 'dismiss' || recommendation.action === 'remind_later') {
      setDismissedInsights([...dismissedInsights, insight.category]);
    } else {
      // Show confirmation and potentially update calculator
      alert(`Action "${recommendation.label}" will be implemented in the next update. This will adjust your calculator settings.`);
      setDismissedInsights([...dismissedInsights, insight.category]);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case 'low': return 'text-green-500 bg-green-500/10 border-green-500/20';
      default: return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertCircle className="w-5 h-5" />;
      case 'medium': return <TrendingUp className="w-5 h-5" />;
      default: return <CheckCircle className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="ml-64 p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#94a3b8]">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (!analytics?.has_data) {
    return (
      <div className="ml-64 p-8">
        <h1 className="text-3xl font-bold text-[#0f172a] mb-6">Analytics & Insights</h1>
        <Card className="border-[#e2e8f0]">
          <CardContent className="pt-8 text-center">
            <Activity className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0f172a] mb-2">No Data Available Yet</h3>
            <p className="text-[#64748b] mb-4">
              {analytics?.message || "Complete at least 1 project to see analytics and insights."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const visibleInsights = analytics.insights.filter(
    insight => !dismissedInsights.includes(insight.category)
  );

  return (
    <div className="ml-64 p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0f172a] mb-2">Analytics & Insights</h1>
        <p className="text-[#64748b]">Performance analysis and recommendations based on {analytics.total_projects} completed project{analytics.total_projects !== 1 ? 's' : ''}</p>
      </div>

      {/* Performance Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-[#e2e8f0]">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-[#64748b]">
              <DollarSign className="w-4 h-4" />
              Total Revenue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#0f172a]">
              ${analytics.summary.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#e2e8f0]">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-[#64748b]">
              <TrendingUp className="w-4 h-4" />
              Total Profit
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${analytics.summary.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${analytics.summary.total_profit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <p className="text-sm text-[#64748b] mt-1">
              {analytics.summary.avg_actual_margin.toFixed(1)}% avg margin
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#e2e8f0]">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-[#64748b]">
              <Target className="w-4 h-4" />
              Bid Accuracy
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#0f172a]">
              {Math.abs(analytics.summary.avg_cost_variance).toFixed(1)}%
            </div>
            <p className="text-sm text-[#64748b] mt-1">
              {analytics.summary.avg_cost_variance > 0 ? 'over' : 'under'} budget
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#e2e8f0]">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-[#64748b]">
              <Activity className="w-4 h-4" />
              Production Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#0f172a]">
              {analytics.summary.avg_lf_per_day.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <p className="text-sm text-[#64748b] mt-1">
              LF/day average
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Calculator Health Check */}
      <Card className="border-[#e2e8f0] mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#0f172a]">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Calculator Health Check
          </CardTitle>
          <CardDescription>Verify your bid calculator assumptions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="text-sm font-medium text-[#64748b] mb-1">Cost Estimation</div>
              <div className="text-lg font-bold text-[#0f172a]">
                {Math.abs(analytics.summary.avg_cost_variance) < 10 ? '✅ Good' : '⚠️ Needs Adjustment'}
              </div>
              <div className="text-xs text-[#64748b] mt-1">
                {Math.abs(analytics.summary.avg_cost_variance).toFixed(1)}% variance from actuals
              </div>
            </div>

            <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="text-sm font-medium text-[#64748b] mb-1">Margin Accuracy</div>
              <div className="text-lg font-bold text-[#0f172a]">
                {Math.abs(analytics.summary.avg_margin_variance) < 5 ? '✅ Good' : '⚠️ Review Pricing'}
              </div>
              <div className="text-xs text-[#64748b] mt-1">
                {analytics.summary.avg_margin_variance > 0 ? '+' : ''}{analytics.summary.avg_margin_variance.toFixed(1)}% vs target
              </div>
            </div>

            <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="text-sm font-medium text-[#64748b] mb-1">Production Efficiency</div>
              <div className="text-lg font-bold text-[#0f172a]">
                {analytics.summary.avg_lf_per_day > 30000 ? '✅ On Track' : '⚠️ Below Target'}
              </div>
              <div className="text-xs text-[#64748b] mt-1">
                {analytics.summary.avg_lf_per_day.toFixed(0)} LF/day vs 36,000 target
              </div>
            </div>

            <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="text-sm font-medium text-[#64748b] mb-1">Time Estimation</div>
              <div className="text-lg font-bold text-[#0f172a]">
                {Math.abs(analytics.summary.avg_days_variance) < 1 ? '✅ Accurate' : '⚠️ Off Target'}
              </div>
              <div className="text-xs text-[#64748b] mt-1">
                {analytics.summary.avg_days_variance > 0 ? '+' : ''}{analytics.summary.avg_days_variance.toFixed(1)} days variance
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Areas for Improvement */}
      {visibleInsights.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Areas for Improvement</h2>
          <div className="space-y-4">
            {visibleInsights.map((insight, index) => (
              <Card key={index} className={`border ${getSeverityColor(insight.severity)}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={getSeverityColor(insight.severity)}>
                        {getSeverityIcon(insight.severity)}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg text-white mb-2">
                          {insight.title}
                        </CardTitle>
                        <CardDescription className="text-gray-300 mb-2">
                          {insight.description}
                        </CardDescription>
                        <div className="text-sm text-white font-medium mb-4">
                          💡 Impact: {insight.impact}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={getSeverityColor(insight.severity)}>
                      {insight.severity}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-white mb-3">Choose an action:</p>
                    {insight.recommendations.map((rec, recIndex) => (
                      <Button
                        key={recIndex}
                        onClick={() => handleActionSelect(insight, rec)}
                        variant="outline"
                        className="w-full justify-start text-left h-auto py-3 hover:bg-gray-700 border-gray-600 text-white"
                      >
                        <span className="text-sm text-white">{rec.label}</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Project Details Table */}
      <Card className="border-[#e2e8f0]">
        <CardHeader>
          <CardTitle className="text-[#0f172a]">Completed Projects Detail</CardTitle>
          <CardDescription>Individual project performance breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e2e8f0]">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[#64748b]">Project</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[#64748b]">County</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#64748b]">Bid Amount</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#64748b]">Actual Cost</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#64748b]">Profit</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#64748b]">Margin</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#64748b]">Days</th>
                </tr>
              </thead>
              <tbody>
                {analytics.projects.map((project, index) => (
                  <tr key={index} className="border-b border-[#e2e8f0] hover:bg-[#f8fafc]">
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#0f172a]">{project.project_name}</div>
                      <div className="text-xs text-[#64748b]">{project.contractor}</div>
                    </td>
                    <td className="py-3 px-4 text-[#0f172a]">{project.county}</td>
                    <td className="py-3 px-4 text-right text-[#0f172a]">
                      ${project.bid_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-3 px-4 text-right text-[#0f172a]">
                      ${project.actual_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td className={`py-3 px-4 text-right font-semibold ${project.actual_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${project.actual_profit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-3 px-4 text-right text-[#0f172a]">
                      {project.actual_margin.toFixed(1)}%
                      {Math.abs(project.margin_variance) > 2 && (
                        <span className={`ml-1 text-xs ${project.margin_variance < 0 ? 'text-red-500' : 'text-green-500'}`}>
                          ({project.margin_variance > 0 ? '+' : ''}{project.margin_variance.toFixed(1)}%)
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-[#0f172a]">
                      {project.actual_days}
                      {project.days_variance !== 0 && (
                        <span className="ml-1 text-xs text-[#64748b]">
                          ({project.days_variance > 0 ? '+' : ''}{project.days_variance})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
